import { createSelector } from 'reselect';
import _ from 'lodash';
import Immutable from 'seamless-immutable';
import { enums } from 'console-kit';

import { Account, Integration, User } from 'common-entities';
import RefData from 'ref-data';

import { FIELD_TYPES } from '../../common/formFields/fieldTypes';
import PageSelectors from '../page/page-selectors';
import AccountSelectors from '../account/account-selectors';
import { prepareDataTypesForMapping } from './utils';
import IntegrationEnums from './integration-enums';

// ################################################################################################################
// INPUT
// ################################################################################################################

const _scopedState = (state) => state.entities.integration;

const _integrations = (state) => _scopedState(state).integrations;
const _integrationIds = createSelector(_integrations, (integrations) => integrations.allIds);
const integrationsById = createSelector(_integrations, (integrations) => integrations.byId);

const _integrationTemplates = (state) => _scopedState(state).integrationTemplates;
const _integrationTemplatesById = createSelector(
  _integrationTemplates,
  (integrationTemplates) => integrationTemplates.byId
);

// ################################################################################################################
// SELECTORS
// ################################################################################################################

const integrations = createSelector([integrationsById], (integrations) => {
  return _.values(integrations);
});
const integrationIds = _integrationIds;

//return a single instance of a integration that matches the query id
const integrationById = createSelector(
  [integrationsById, (state, integrationId) => integrationId],
  (integrations, integrationId) => integrations[integrationId]
);
const isFetchingIntegrations = (state) => _integrations(state).isFetching;

/**
 * returns a filtered list of integration templates that are available to the user
 */

const integrationTemplates = (state) => {
  const integrationTemplates = _integrationTemplatesById(state);
  const canCreateIntegrations = User.Selectors.canCreateIntegration(state);

  const restrictedIntegrationTemplates = Integration.Enums.RESTRICTED_INTEGRATION_TEMPLATES;

  return _.values(integrationTemplates).filter((integrationTemplate) => {
    const isRestricted = restrictedIntegrationTemplates.includes(integrationTemplate.typeRef.id);
    return !isRestricted || (isRestricted && canCreateIntegrations);
  });
};

const isFetchingIntegrationTemplates = (state) => _integrationTemplates(state).isFetching;

const listIntegrationsContainerRef = (state, containerRef) => {
  const _account = Account.Selectors.activeAccount(state);
  const _libraryContainerRef = `library:${_account.id}:0`;
  if (_libraryContainerRef === containerRef) return containerRef;
  return `library:${_account.id}:0,${containerRef}`;
};

const listIntegrationsClassRef = (state, classRef) => {
  const _libraryClassrRef = RefData.Enum.CLASS.INTEGRATION.CAMPAIGN;
  if (_libraryClassrRef === classRef) return classRef + `,${RefData.Enum.CLASS.INTEGRATION.ACCOUNT}`;
  return classRef;
};

const integrationsByClassRef = createSelector([integrations], (integrations) => {
  return _.groupBy(integrations, function (integration) {
    return integration.classRef.id;
  });
});

// ################################################################################################################
// WIZARD INTEGRATION SELECTORS
// ################################################################################################################

const wizardIntegration = (state) => _integrations(state).wizardIntegration;
const wizardFetchingMappingData = (state) => wizardIntegration(state).mappingData.isFetching;

const wizardFetchingDataSources = createSelector(
  [wizardIntegration, (state, stepDefinition) => stepDefinition],
  (_wizardIntegration, stepDefinition) => {
    const stepFieldsIds = stepDefinition.fields.map((field) => field.id);
    const _isFetching = _.some(Object.keys(_wizardIntegration.dataSources), (key) => {
      return stepFieldsIds.includes(key) && _wizardIntegration.dataSources[key].isFetching;
    });
    return _isFetching;
  }
);

const wizardStepFieldsConfiguration = createSelector(
  [wizardIntegration, (state, stepIdx) => stepIdx],
  (_wizardIntegration, stepIdx) => _wizardIntegration.steps[stepIdx].fields
);

const wizardMappingData = createSelector([wizardIntegration], (_wizardIntegration) =>
  prepareDataTypesForMapping(_wizardIntegration.mapping)
);

const wizardRequestData = createSelector(
  [wizardIntegration, wizardMappingData],
  (_wizardIntegration, _wizardMappingData) => {
    const _wizardRequestData = _wizardIntegration.fields;
    return _wizardRequestData.set('mapping', _wizardMappingData);
  }
);

const wizardUpdateObject = createSelector(
  [wizardRequestData, (state, integration) => integration],
  (_wizardRequestData, integration) => {
    const _integration = integration.set('name', _wizardRequestData.name);
    const _instanceConfig = integration.getIn(['instanceConfig']);
    return _integration.set('instanceConfig', {
      ..._instanceConfig,
      blueprintData: _wizardRequestData,
    });
  }
);

const wizardBlueprintStepsByType = createSelector(
  [wizardIntegration, (state, type) => type],
  (_wizardIntegration, type) => _wizardIntegration.steps.filter((step) => step.type === type)
);

const wizardBlueprintFieldsByType = createSelector([wizardBlueprintStepsByType], (_wizardBlueprintStepsByType) =>
  _wizardBlueprintStepsByType.map((step) => step.fields).flat()
);

/**
 * Returns an array of {id, name} objects of remote field name values (excluding policy and submit)
 */
const getRemoteFieldDataSource = createSelector(PageSelectors.getRemoteFieldIdsForIntegrations, (remoteFieldIds) =>
  Immutable(
    _.map(_.without(remoteFieldIds, FIELD_TYPES.POLICY, FIELD_TYPES.SUBMIT), (remoteField) => ({
      id:   remoteField,
      name: remoteField,
    }))
  )
);

/**
 * Returns datasource for both hidden fields and remote field ids
 */
const getRemoteHiddenFieldDataSource = createSelector(
  getRemoteFieldDataSource,
  PageSelectors.getHiddenFields,
  (remoteFieldDataSource, hiddenFields) =>
    Immutable([
      ...remoteFieldDataSource,
      ..._.map(hiddenFields, (hiddenField) => ({
        id:   hiddenField.name,
        name: hiddenField.name,
      })),
    ])
);

/**
 * Returns the remote fields and hidden fields data, experience metadata fields and hardcoded values for the entry, campaign, account
 * to be used in the new customised self-serve integrations
 */
const getSelfServeIntegrationDataSource = createSelector(
  PageSelectors.getRemoteFieldIdsForIntegrations,
  PageSelectors.getHiddenFields,
  PageSelectors.getOptInFields,
  AccountSelectors.activeAccount,
  (remoteFieldIds, hiddenFields, optInFields, account) => {
    const selfServeDataSource = [
      { id: 'entry', name: 'Entry', disabled: true },
      // this list of fields is used only in this place so far. If in future we will need it, it can be moved to some enums
      ..._.map(['email', 'firstname', 'lastname', ...optInFields], (field) => ({
        id:     `entry.${field}`,
        name:   field,
        indent: enums.sizes.SMALL,
      })),
    ];

    const remoteFields = _.without(
      remoteFieldIds,
      FIELD_TYPES.POLICY,
      FIELD_TYPES.SUBMIT,
      FIELD_TYPES.NAME,
      FIELD_TYPES.EMAIL
    );

    if (remoteFields.length || hiddenFields.length) {
      const paramsDataSource = [
        // category
        { id: 'params', name: 'Params', disabled: true },
        // remote fields
        ..._.map(remoteFields, (remoteField) => ({
          id:     `params.${remoteField}`,
          name:   remoteField,
          indent: enums.sizes.SMALL,
        })),
        // hidden fields
        ..._.map(hiddenFields, (hiddenField) => ({
          id:     `params.${hiddenField.name}`,
          name:   hiddenField.name,
          indent: enums.sizes.SMALL,
        })),
      ];

      selfServeDataSource.push(...paramsDataSource);
    }

    selfServeDataSource.push(...IntegrationEnums.campaignDataSource);
    selfServeDataSource.push(...IntegrationEnums.accountDataSource);

    const experienceMetadataIntegrationFields = _.filter(_.get(account, 'experienceMetadata.fields', []), (field) =>
      _.includes(field.visibility, 'integration')
    );
    if (experienceMetadataIntegrationFields && Object.values(experienceMetadataIntegrationFields).length) {
      const metadataDataSource = [
        { id: 'experienceMetadata', name: 'Experience Metadata', disabled: true },
        ..._.map(Object.values(experienceMetadataIntegrationFields), (metadataField) => ({
          id:     `experienceMetadata.${metadataField.name}`,
          name:   metadataField.title,
          indent: enums.sizes.SMALL,
        })),
      ];

      selfServeDataSource.push(...metadataDataSource);
    }

    return selfServeDataSource;
  }
);

const credentials = createSelector(_integrations, (integrations) => _.values(integrations.credentialsById));
const selectedCredentialsById = (id) =>
  createSelector(credentials, (_credentials) => _credentials.find((c) => c.id === id));
const isFetchingIntegrationCredentials = createSelector(
  _integrations,
  (integrations) => integrations.isFetchingCredentials
);

// we need to return alternative images(white variants) for the black background inside the picker
// and this selector temporary mutates the objects to have a white background for the picker
// in the API request to create a new integration we only send for the ID
const invertedIntegrationTemplates = createSelector(integrationTemplates, (integrationTemplates) =>
  integrationTemplates.map((integration) => {
    //This is an edge case where developer can create in the M console, integrations. These integrations should not
    //be released to production, but can cause inconvenience for local or test environments.
    const integrationIconPath = _.get(integration, 'iconPath', undefined);

    if (
      integrationIconPath &&
      IntegrationEnums.INVERTED_INTEGRATION_LOGOS.some((whiteAsset) => integrationIconPath.includes(whiteAsset))
    ) {
      // the object coming in is an immutable, we cannot spread the object
      // because we loose the model specific functions
      return integration.set('iconPath', integrationIconPath.replace(/\.svg$/, '-white.svg'));
    }

    return integration;
  })
);

export default {
  integrations,
  integrationIds,
  integrationById,
  integrationsById,
  isFetchingIntegrations,

  listIntegrationsContainerRef,
  listIntegrationsClassRef,

  integrationsByClassRef,

  integrationTemplates,
  invertedIntegrationTemplates,
  isFetchingIntegrationTemplates,

  wizardIntegration,
  wizardFetchingMappingData,
  wizardFetchingDataSources,
  wizardStepFieldsConfiguration,
  wizardRequestData,
  wizardUpdateObject,
  wizardBlueprintStepsByType,
  wizardBlueprintFieldsByType,

  getRemoteFieldDataSource,
  getRemoteHiddenFieldDataSource,
  getSelfServeIntegrationDataSource,

  credentials,
  selectedCredentialsById,
  isFetchingIntegrationCredentials,
};
